<template>
  <div
    v-if="style"
    class="style"
  >
    <Header no-shadow>
      <ElButton
        v-if="!pdfExport"
        slot="header-left"
        class="style__close"
        type="text"
        @click="close"
      >
        <i class="el-icon-arrow-left" /> Go back
      </ElButton>

      <h3
        slot="header-center"
        class="style__name"
      >
        {{ style.name }}
      </h3>

      <ElPopover
        v-if="!pdfExport"
        slot="header-right"
        v-model="showNotes"
        v-click-outside="hideNotes"
        width="300"
        placement="bottom-end"
        :append-to-body="false"
        trigger="manual"
      >
        <Header
          v-if="ratingMode"
          class="style__ratings"
          no-shadow
        >
          <div
            v-for="ratingOption in ratingOptions"
            :key="ratingOption.value"
            :class="[
              'style__rating',
              { selected: currentStyle.rating === ratingOption.value }
            ]"
            @click="setRating(ratingOption.value)"
          >
            <img
              :src="ratingOption.imgUrl"
              alt="rating icon"
            >
            <div>{{ ratingOption.text }}</div>
          </div>
        </Header>
        <section class="style__notes-wrapper">
          <textarea
            ref="notesInput"
            class="style__notes"
            :placeholder="ratingMode ? 'Add a comment...' : 'Add notes...'"
            @keyup="processText($event)"
            @input="setElementAutoHeight($refs.notesInput)"
            v-text="ratingMode ? currentStyle.comment : currentStyle.notes"
          />
          <div
            :class="[
              {
                'style__notes--is-saving': showSaveNotice
              },
              'style__notes--saved'
            ]"
          >
            <small>saved</small>
          </div>
        </section>

        <ElButton
          v-if="isUserCreatorInGroup || ratingMode"
          slot="reference"
          class="style__notes-trigger"
          type="text"
          @click="toggleNotes"
        >
          {{ notesToggleText }}
        </ElButton>
      </ElPopover>
    </Header>

    <section class="style__body">
      <div class="style__color-run">
        <aside
          v-if="showSliderControls"
          class="style__navigation-left"
        >
          <ElButton
            circle
            @click="triggerNavigationLeft"
          >
            <i class="el-icon-arrow-left" />
          </ElButton>
        </aside>

        <StylePreview
          :style-id="styleId"
          no-text
          enable-image-click
          :image-resolution="imageResolution"
        />

        <aside
          v-if="showSliderControls"
          class="style__navigation-right"
        >
          <ElButton
            circle
            @click="triggerNavigationRight"
          >
            <i class="el-icon-arrow-right" />
          </ElButton>
        </aside>
      </div>
      <div
        v-if="shouldDisplayFooter"
        class="style__tags"
      >
        <div class="stitch-list-spaced-horizontal">
          <div
            v-if="style.assets_label"
            class="style__label"
          >
            <img
              crossorigin="use-credentials"
              :src="labelImage"
            >
          </div>
          <ElTag
            v-for="(metaEntry, metaEntryIndex) in meta"
            :key="metaEntryIndex"
            type="info"
          >
            {{ metaEntry }}
          </ElTag>
          <ElTag
            v-for="tag in style.custom_tags"
            :key="`tag-${tag.id}`"
            type="info"
          >
            {{ tag.text }}
          </ElTag>
        </div>
      </div>
      <div
        v-if="details.length > 0 && !pdfExport"
        class="style__details"
      >
        <div class="style__details-wrapper">
          <div
            v-for="detail in details"
            :key="detail.id"
            class="style__detail"
            :style="
              `backgroundImage: url('${getDetailImageUrlTrimmedSmall(
                detail.assets_file
              )}')`
            "
            @click="
              activeDetailImageUrl = getDetailImageUrlTrimmedLarge(
                detail.assets_file
              )
            "
          />
        </div>
        <StitchDialog
          append-to-body
          :visible="activeDetailImageUrl !== null"
          @close="closeDetailImageDialog"
        >
          <StitchZoom
            v-loading="isLoadingDetailImage"
            :img-normal="activeDetailImageUrl ? activeDetailImageUrl : null"
            :scale="1.5"
            @loaded="disableDetailImageLoading()"
          />
        </StitchDialog>
      </div>
      <div
        v-if="details.length > 0 && pdfExport"
        class="style__details--pdf-export"
      >
        <div
          v-for="detail in details"
          :key="detail.id"
          class="style__detail--pdf-export"
        >
          <img
            crossorigin="use-credentials"
            :src="getDetailImageUrlTrimmedLarge(detail.assets_file)"
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import debounce from 'debounce'

import Utils from '@/services/utils'
import { imageResolutions } from '@/constants/image'
import Header from '@/components/Header.vue'
import StylePreview from '@/components/project/StylePreview.vue'

import { S3Trim } from '@/mixins/s3Trim.js'
import { ElementAutoHeight } from '@/mixins/ElementAutoHeight.js'

import ClickOutside from 'element-ui/src/utils/clickoutside'

export default {
  name: 'StyleDetailsNew',

  components: {
    Header,
    StylePreview
  },

  directives: {
    ClickOutside
  },

  mixins: [S3Trim, ElementAutoHeight],

  props: {
    styleId: {
      type: Number,
      required: true
    },
    groupId: {
      type: Number,
      required: true
    },
    showSliderControls: {
      type: Boolean,
      default: false
    },
    ratingMode: {
      type: Boolean,
      default: false
    },
    pdfExport: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    isLoadingDetailImage: true,
    showNotes: false,
    ratingOptions: [
      {
        text: 'Strong',
        imgUrl: require('../../assets/svg/ratings/star.svg'),
        value: 3
      },
      {
        text: 'OK',
        imgUrl: require('../../assets/svg/ratings/check.svg'),
        value: 2
      },
      {
        text: 'Weak',
        imgUrl: require('../../assets/svg/ratings/oblique.svg'),
        value: 1
      },
      {
        text: 'No',
        imgUrl: require('../../assets/svg/ratings/cross.svg'),
        value: 0
      }
    ],
    activeDetailImageUrl: null,
    showSaveNotice: false,
    typingTimer: null,
    lastSaveText: null,
    typingInterval: 2000,
    toggleSaveNoticeInterval: 800,
    currentStyle: {
      comment: '',
      notes: '',
      rating: null
    }
  }),

  computed: {
    ...mapGetters([
      'getStyleById',
      'getOptionsByStyleId',
      'getDetailsByStyleId',
      'getUser'
    ]),

    /**
     * @returns {object}
     */
    style () {
      return this.getStyleById(this.styleId)
    },

    /**
     * @returns {Array}
     */
    options () {
      return this.getOptionsByStyleId(this.styleId)
    },

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.DETAIL_FULL
    },

    /**
     * @returns {object}
     */
    details () {
      return this.getDetailsByStyleId(this.styleId)
    },

    /**
     * @returns {object}
     */
    userFeedback () {
      return this.style.feedback.find(
        feedback => feedback.user.id === this.user.id
      )
    },

    /**
     * @returns {object}
     */
    meta () {
      return Utils.parseStyleMetaNames(this.style)
    },

    /**
     * @returns {object}
     */
    user () {
      return this.getUser()
    },

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.groupId)
    },

    /**
     * @returns {string}
     */
    notesToggleText () {
      if (this.ratingMode) {
        return this.showNotes ? 'Hide Comment' : 'Show Comment'
      } else {
        return this.showNotes ? 'Hide Notes' : 'Show Notes'
      }
    },

    /**
     * @returns {boolean}
     */
    shouldDisplayFooter () {
      return (
        this.style.assets_label ||
        this.meta.length > 0 ||
        this.style.custom_tags.length > 0
      )
    },

    /**
     * @returns {string | null}
     */
    labelImage () {
      if (this.style.assets_label) {
        return this.s3Trim(this.style.assets_label)
      } else {
        return null
      }
    }
  },

  created () {
    this.setInitialData()
  },

  methods: {
    ...mapActions(['createFeedback', 'updateFeedback', 'updateStyle']),

    /**
     */
    setInitialData () {
      if (this.ratingMode) {
        if (this.userFeedback) {
          this.currentStyle.comment = this.userFeedback.comment
          this.currentStyle.rating = this.userFeedback.rating
        }
      } else {
        this.currentStyle.notes = this.style.notes
      }
    },

    /**
     * @param   {string} imageUrl
     *
     * @returns {string}
     */
    getDetailImageUrlTrimmedSmall (imageUrl) {
      return this.s3Trim(
        imageUrl,
        this.pdfExport ? imageResolutions.DETAIL_FULL : imageResolutions.DETAIL
      )
    },

    /**
     * @param   {string} imageUrl
     *
     * @returns {string}
     */
    getDetailImageUrlTrimmedLarge (imageUrl) {
      return this.s3Trim(imageUrl, imageResolutions.ZOOM)
    },

    /**
     */
    disableDetailImageLoading () {
      this.isLoadingDetailImage = false
    },

    /**
     */
    closeDetailImageDialog () {
      this.activeDetailImageUrl = null
      this.isLoadingDetailImage = true
    },

    /**
     */
    close () {
      this.$emit('close')
    },

    /**
     * @param {number} ratingValue
     */
    async setRating (ratingValue) {
      if (ratingValue === this.currentStyle.rating) {
        this.currentStyle.rating = null
      } else {
        this.currentStyle.rating = ratingValue
      }

      await this.saveFeedback({
        rating: this.currentStyle.rating
      })
    },

    /**
     * @param {object} payload
     */
    async saveFeedback (payload) {
      payload.styleId = this.styleId

      if (this.userFeedback) {
        payload.feedbackId = this.userFeedback.id

        await this.updateFeedback(payload)
      } else {
        await this.createFeedback(payload)
      }
    },

    processText: debounce(
      /**
       * @param {Event} event
       */
      function (event) {
        const currentText = event.target.value
        const payload = {}

        if (this.ratingMode) {
          payload.comment = currentText
          this.currentStyle.comment = payload.comment

          this.saveFeedback(payload)
        } else {
          payload.styleId = this.styleId
          payload.notes = currentText
          this.currentStyle.notes = payload.notes
          this.updateStyle(payload)
        }

        this.toggleSaveMessageNotice()
      }, 300),

    /**
     */
    toggleNotes () {
      this.showNotes = !this.showNotes

      setTimeout(() => {
        this.setElementAutoHeight(this.$refs.notesInput)
      }, 100)
    },

    /**
     */
    toggleSaveMessageNotice () {
      window.clearTimeout(this.typingTimer)

      this.typingTimer = window.setTimeout(this.doneTyping, this.typingInterval)
    },

    /**
     */
    doneTyping () {
      this.showSaveNotice = !this.showSaveNotice

      window.setTimeout(() => {
        this.showSaveNotice = !this.showSaveNotice
      }, this.toggleSaveNoticeInterval)
    },

    /**
     */
    hideNotes () {
      this.showNotes = false
    },

    /**
     */
    triggerNavigationLeft () {
      this.$emit('click-navigation-left')
    },

    /**
     */
    triggerNavigationRight () {
      this.$emit('click-navigation-right')
    }
  }
}
</script>

<style lang="scss" scoped>
$size-detail-small: 260px;
$width-label: 150px;
$min-height-detail-big: 400px;
$opacity-message-is-saving: 0.8;
$opacity-rating-default: 0.3;
$opacity-rating-selected: 1;
$textarea-max-height: 80vh;
$scrollbar-width: 8px;
$scrollbar-border-radius: 10px;
$min-height-zoom-hover: 65vh;

.style {
  @include size(100%);

  display: flex;
  flex-direction: column;
}

.style__name {
  @include text-ellipsis;

  text-transform: uppercase;
}

.style__body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.style__color-run {
  display: flex;
  height: 100%;
  margin-bottom: $spacer-unit-x2;
}

.style__navigation-left {
  @include absolute-center-left;

  left: $gutter;
}

.style__navigation-right {
  @include absolute-center-right;

  right: $gutter;
}

.style__tags {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: $spacer-unit-x4;
}

.style__label {
  height: $spacer-unit-x4;

  img {
    align-self: center;
    max-width: 100%;
    height: auto;
    max-height: 100%;
  }
}

.style__details {
  display: flex;
  justify-content: center;
  width: 100%;
}

.style__details--pdf-export {
  display: grid;
  grid-auto-rows: $size-detail-small;
  grid-auto-flow: column;
  grid-gap: $spacer-unit;
}

.style__details-wrapper {
  @include scroll-x-smooth;

  display: grid;
  grid-auto-rows: $size-detail-small;
  grid-auto-columns: $size-detail-small;
  grid-auto-flow: column;
  grid-gap: $spacer-unit-x2;
  scroll-snap-type: x proximity;

  @media only screen and (max-height: $max-height-list-item) {
    grid-auto-rows: $width-template-item / 2;
  }

  @media only screen and (max-height: $height-list-item *2) {
    grid-auto-rows: $height-template-item;
  }
}

.style__detail {
  background-color: $grey-extra-lighter;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: pointer;
  scroll-snap-align: start;
}

.style__detail--pdf-export {
  display: flex;
  justify-content: center;
  background-color: $grey-extra-lighter;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.style__ratings {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: auto;
  margin-bottom: $spacer-unit-x2;
  padding-top: $spacer-unit;
}

.style__rating {
  padding: 0 $spacer-unit;
  text-align: center;
  cursor: pointer;
  opacity: $opacity-rating-default;
  transition: opacity $fast-ease;

  &:hover,
  &.selected {
    opacity: $opacity-rating-selected;
  }
}

.style__notes {
  max-height: $textarea-max-height;
  padding-bottom: $gutter;
  overflow: hidden;
  overflow-y: auto;
  color: $grey-lighter;
  word-break: break-word;
  cursor: text;
  resize: none;
  caret-color: $blue;

  &:focus {
    outline: none;
  }

  &[contenteditable='true']:empty::before {
    display: block; /* For Firefox */
    color: $grey-light;
    content: attr(placeholder);
  }
}

.style__notes--saved {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: $blue;
  opacity: 0;
  transition: opacity 0.8s;
  user-select: none;

  &.style__notes--is-saving {
    opacity: $opacity-message-is-saving;
  }

  .style__icon-check {
    margin-right: spacing(0.5);
  }
}

.style__notes-wrapper {
  position: relative;

  /deep/ {
    ::-webkit-scrollbar,
    ::-webkit-scrollbar-track,
    ::-webkit-scrollbar-thumb {
      display: inline;
    }

    .style__notes::-webkit-scrollbar {
      width: $scrollbar-width;
    }

    .style__notes::-webkit-scrollbar-track {
      background: $white;
    }

    .style__notes:hover::-webkit-scrollbar-thumb {
      background: rgba(205, 205, 205, 0.6);
      border-radius: $scrollbar-border-radius;
    }

    .style__notes:hover::-webkit-scrollbar-thumb:hover {
      background: rgba(205, 205, 205, 0.8);
    }
  }
}

/deep/ .zoom-on-hover {
  min-height: $min-height-zoom-hover;
}
</style>
